/**
 * Build a Stripe dashboard deep link for a given Stripe object ID.
 *
 * Used by the admin Financials tab so that operators answering "what's
 * going on with this payment?" can click through to the Stripe dashboard
 * instead of pasting the ID into the URL bar by hand.
 *
 * Behaviour:
 *   - The ID prefix (`pi_`, `ch_`, `re_`, `cs_`, `in_`, `py_`) decides which
 *     dashboard section to deep-link into. Anything we don't recognise
 *     returns null so the caller can fall back to plain text instead of
 *     building a 404 link.
 *   - Test vs live mode is inferred from `VITE_STRIPE_PUBLISHABLE_KEY`
 *     (already used by StripeCheckoutDialog) — `pk_test_*` → /test/ path,
 *     `pk_live_*` → live path. No separate flag to keep in sync.
 *   - The Stripe account ID (`acct_*`) is included in the path when
 *     configured via `VITE_STRIPE_ACCOUNT_ID`. Stripe accepts the URL
 *     without it (defaults to the operator's last-viewed account), so the
 *     env var is optional — handy in environments where the account ID
 *     hasn't been recorded yet.
 *
 * Publishable + account values are baked into the bundle at build time
 * (Vite inlines `import.meta.env.*`), so this util is safe to call from
 * any client component without an extra round-trip.
 */

const PREFIX_TO_PATH: Record<string, string> = {
  // PaymentIntent → /payments/pi_xxx (what the user asked for)
  pi: 'payments',
  // Charges and refunds — Stripe dashboard renders them under /payments/
  // (the charge view) and /payments/<pi>?refund=re_xxx respectively. We
  // route both to /payments/ which is the closest single-page view.
  ch: 'payments',
  py: 'payments',
  re: 'payments',
  // Checkout Session and Invoice deep links — included so the util is
  // generally useful and so future callers don't have to special-case.
  cs: 'payments',
  in: 'invoices',
};

function getPrefix(id: string): string | null {
  const idx = id.indexOf('_');
  if (idx <= 0) return null;
  return id.slice(0, idx);
}

function isTestMode(): boolean {
  const pk = (import.meta as any).env?.VITE_STRIPE_PUBLISHABLE_KEY as
    | string
    | undefined;
  if (typeof pk === 'string' && pk.startsWith('pk_live_')) return false;
  // Default to test mode when the key is missing or in the test family.
  // Linking into /test/ when prod is actually live is harmless (Stripe
  // redirects to live), but the inverse can hide the row the operator is
  // hunting for, so the conservative default is /test/.
  return true;
}

function getAccountSegment(): string {
  const acct = (import.meta as any).env?.VITE_STRIPE_ACCOUNT_ID as
    | string
    | undefined;
  if (typeof acct === 'string' && /^acct_[A-Za-z0-9]+$/.test(acct)) {
    return `/${acct}`;
  }
  return '';
}

/**
 * Returns a Stripe dashboard URL for the given object ID, or null when the
 * ID is empty / unrecognised. Callers should render the plain ID when null
 * is returned rather than building a broken link.
 */
export function buildStripeDashboardUrl(
  stripeId: string | null | undefined,
): string | null {
  if (!stripeId) return null;
  const prefix = getPrefix(stripeId);
  if (!prefix) return null;
  const section = PREFIX_TO_PATH[prefix];
  if (!section) return null;

  const account = getAccountSegment();
  const mode = isTestMode() ? '/test' : '';
  return `https://dashboard.stripe.com${account}${mode}/${section}/${stripeId}`;
}
